import { useDispatch, useSelector } from 'react-redux';
import { getMachine } from 'lib/selectors/getMachine';
import { getScopeStart } from 'lib/selectors/getScopeStart';
import { getShift } from 'lib/selectors/getShift';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import {
  actionSetLastNonIdle,
  actionSetMachineOperationalStatus,
} from 'lib/actions';
import { getWaitingForWorkEnabled } from 'lib/selectors/getWaitingForWorkEnabled';
import { useCallback, useEffect } from 'react';
import { getHasOpenActivitySet } from 'lib/selectors/getHasOpenActivitySet';
import { getHasActiveOperatorRun } from 'lib/selectors/getHasActiveOperatorRun';
import { getMachineOperationalStatus } from 'lib/selectors/getMachineOperationalStatus';
import { getLatestNonIdleInterval } from 'lib/selectors/getLatestNonIdleInterval';
import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { getLatestOperatorRun } from 'lib/selectors/getLatestOperatorRun';
import { useDebouncedLazyQuery } from './useDebouncedLazyQuery';
import { GET_MACHINE_OPERATIONAL_STATUS } from '../api/queries';

export const useMachineOperationalStatus = () => {
  const dispatch = useDispatch();

  const machine = useSelector(getMachine);
  const scopeStart = useSelector(getScopeStart);
  const shift = useSelector(getShift);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const waitingForWorkEnabled = useSelector(getWaitingForWorkEnabled);
  const hasOpenActivitySet = useSelector(getHasOpenActivitySet);
  const hasActiveOperatorRun = useSelector(getHasActiveOperatorRun);
  const machineOperationalStatus = useSelector(getMachineOperationalStatus);
  const latestActivity = useSelector(getLatestActivity);
  const latestNonIdleInterval = useSelector(getLatestNonIdleInterval);
  const latestOperatorRun = useSelector(getLatestOperatorRun);

  // Skip fetch if we have both an open activity set and an active operator run,
  // as we know we're not waiting for operator
  const shouldSkip =
    !waitingForWorkEnabled || (hasOpenActivitySet && hasActiveOperatorRun);

  const [requestMachineOperationalStatus] = useDebouncedLazyQuery(
    GET_MACHINE_OPERATIONAL_STATUS,
    {
      variables: {
        machineRef: machine.machineRef,
        input: {
          startDate: scopeStart,
          shift,
          ...(latestActivitySet && {
            activitySet: {
              ...latestActivitySet,
              activities: latestActivity ? [latestActivity] : [],
            },
          }),
          lastNonIdleInterval: latestNonIdleInterval,
          lastOperatorRun: latestOperatorRun,
        },
      },
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        if (!data || typeof data !== 'object') {
          return;
        }

        const payload = data.machineOperationalStatus;
        if (!payload || typeof payload !== 'object') {
          return;
        }

        dispatch(actionSetLastNonIdle(payload.lastNonIdleInterval));
        dispatch(
          actionSetMachineOperationalStatus({
            status: payload.status,
            machineRef: payload.machineRef,
            since: payload.since,
          })
        );
      },
    },
    3000
  );

  useEffect(() => {
    if (shouldSkip && machineOperationalStatus) {
      dispatch(actionSetMachineOperationalStatus(null));
    }
  }, [shouldSkip, machineOperationalStatus, dispatch]);

  const conditionalRequestMachineOperationalStatus = useCallback(() => {
    if (shouldSkip) {
      return Promise.resolve();
    }
    return requestMachineOperationalStatus();
  }, [shouldSkip, requestMachineOperationalStatus]);

  return conditionalRequestMachineOperationalStatus;
};
